import { useEffect, useMemo, useRef, useState } from "react";
import phoneCountries from "../../../catalogs/phoneCountries";
import "./CustomerFormModal.css";

const normalizePhoneCode = (code) => {
  const c = String(code ?? "").trim();
  if (!c) return "";
  return c.startsWith("+") ? c : `+${c}`;
};

function CustomerFormModal({ isOpen, onClose, customerData, onSubmit }) {
  const isEdit = !!customerData;

  const initialForm = useMemo(
    () => ({
      nombre: customerData?.nombre || "",
      apellido: customerData?.apellido || "",
      codigo_telefono: customerData?.codigo_telefono || "",
      telefono: customerData?.telefono || "",
      correo: customerData?.correo || "",
    }),
    [customerData]
  );

  const [form, setForm] = useState(initialForm);
  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const codeRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    setForm(initialForm);
    setAlert(null);
    setSaving(false);

    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, [initialForm, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const showAlert = (type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4000);
  };

  const validatePhoneCode = (code) => {
    const c = String(code ?? "").trim();
    if (!c) return true;
    return /^\+?\d{1,6}$/.test(c);
  };

  const validateEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const updateField = (e) => {
    const { name, value } = e.target;

    if (name === "codigo_telefono") {
      setForm((prev) => ({ ...prev, [name]: normalizePhoneCode(value) }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const selectCode = (value) => {
    setForm((prev) => ({ ...prev, codigo_telefono: normalizePhoneCode(value) }));
    setCodeOpen(false);
  };

  // Cierra el desplegable de código de país al hacer clic fuera o con Escape.
  useEffect(() => {
    if (!codeOpen) return undefined;
    const onDown = (e) => {
      if (codeRef.current && !codeRef.current.contains(e.target)) setCodeOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setCodeOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [codeOpen]);

  const countryList = useMemo(() => {
    const arr = Array.isArray(phoneCountries) ? phoneCountries : [];
    return arr
      .filter((c) => c?.code)
      .map((c) => ({
        value: normalizePhoneCode(c.code),
        country: c.country || "",
        iso: c.iso || "",
        flag: c.flag || "",
      }))
      .sort((a, b) => a.country.localeCompare(b.country));
  }, []);

  const selectedCountry = useMemo(() => {
    const code = normalizePhoneCode(form.codigo_telefono);
    if (!code) return null;
    return (Array.isArray(phoneCountries) ? phoneCountries : []).find(
      (c) => normalizePhoneCode(c?.code) === code
    );
  }, [form.codigo_telefono]);

  const phoneMin = selectedCountry?.minLength ?? undefined;
  const phoneMax = selectedCountry?.maxLength ?? undefined;

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    setAlert(null);

    const nombreN = String(form.nombre || "").trim();
    const apellidoN = String(form.apellido || "").trim();
    const correoN = String(form.correo || "").trim();
    const telefonoN = String(form.telefono || "").trim();
    const codigoTelN = normalizePhoneCode(form.codigo_telefono);

    if (!nombreN) return showAlert("error", "El nombre es obligatorio.");
    if (!apellidoN) return showAlert("error", "El apellido es obligatorio.");
    if (correoN && !validateEmail(correoN))
      return showAlert("error", "El correo no es válido.");

    if (codigoTelN && !validatePhoneCode(codigoTelN)) {
      return showAlert("error", "El código de país no es válido. Ejemplo: +502");
    }

    if (telefonoN && phoneMin && telefonoN.replace(/\D/g, "").length < phoneMin) {
      return showAlert(
        "error",
        `El teléfono debe tener al menos ${phoneMin} dígitos para este país.`
      );
    }
    if (telefonoN && phoneMax && telefonoN.replace(/\D/g, "").length > phoneMax) {
      return showAlert(
        "error",
        `El teléfono debe tener como máximo ${phoneMax} dígitos para este país.`
      );
    }

    const payload = {
      nombre: nombreN,
      apellido: apellidoN,
      correo: correoN,
      telefono: telefonoN,
      codigo_telefono: codigoTelN || "",
    };

    setSaving(true);

    try {
      await onSubmit(payload);
      onClose?.();
    } catch (error) {
      console.error("Customer save failed:", error);
      showAlert("error", error?.message || "No se pudo guardar el cliente.");
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="tienda-modal__overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div className="tienda-modal tienda-modal--shell tienda-modal--cliente" onMouseDown={(e) => e.stopPropagation()}>
        <div className="tienda-modal__header">
          <div className="tienda-modal__header-text">
            <h2 className="tienda-modal__title">{isEdit ? "Editar cliente" : "Crear cliente"}</h2>
            <p className="tienda-modal__subtitle">Registra los datos de contacto del cliente.</p>
          </div>
          <button
            type="button"
            className="tienda-modal__close"
            onClick={onClose}
            disabled={saving}
            title="Cerrar"
          >
            ✕
          </button>
        </div>

        {alert && (
          <div className={`tienda-modal__alert tienda-modal__alert--${alert.type === "error" ? "error" : "info"}`}>
            <span>{alert.message}</span>
            <button
              type="button"
              className="tienda-modal__alert-x"
              onClick={() => setAlert(null)}
              disabled={saving}
              title="Cerrar alerta"
            >
              ✕
            </button>
          </div>
        )}

        <form onSubmit={submit} className="tienda-modal__body">
          <div className="tienda-modal__form-grid">
          <div className="tienda-modal__field">
            <label className="tienda-modal__label">Nombre</label>
            <input
              name="nombre"
              value={form.nombre}
              onChange={updateField}
              required
              disabled={saving}
              autoFocus
              className="tienda-modal__input"
            />
          </div>

          <div className="tienda-modal__field">
            <label className="tienda-modal__label">Apellido</label>
            <input
              name="apellido"
              value={form.apellido}
              onChange={updateField}
              required
              disabled={saving}
              className="tienda-modal__input"
            />
          </div>

            <div className="tienda-modal__field">
              <label className="tienda-modal__label">Código de país</label>
              <div className="tienda-modal__cc" ref={codeRef}>
                <button
                  type="button"
                  className={`tienda-modal__cc-trigger${codeOpen ? " is-open" : ""}`}
                  onClick={() => !saving && setCodeOpen((o) => !o)}
                  disabled={saving}
                  aria-haspopup="listbox"
                  aria-expanded={codeOpen}
                  title="Código de país"
                >
                  {selectedCountry ? (
                    <span className="tienda-modal__cc-value">
                      <span className="tienda-modal__cc-flag">
                        {selectedCountry.flag}
                      </span>
                      <span className="tienda-modal__cc-name">
                        {selectedCountry.country}
                      </span>
                      <span className="tienda-modal__cc-dial">
                        {normalizePhoneCode(selectedCountry.code)}
                      </span>
                    </span>
                  ) : (
                    <span className="tienda-modal__cc-placeholder">
                      Selecciona un código...
                    </span>
                  )}
                  <span className="tienda-modal__cc-chevron" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </span>
                </button>

                {codeOpen && (
                  <div className="tienda-modal__cc-menu" role="listbox">
                    {countryList.map((c) => {
                      const selected =
                        c.value === normalizePhoneCode(form.codigo_telefono);
                      return (
                        <button
                          type="button"
                          key={`${c.value}-${c.iso}`}
                          role="option"
                          aria-selected={selected}
                          className={`tienda-modal__cc-option${
                            selected ? " is-selected" : ""
                          }`}
                          onClick={() => selectCode(c.value)}
                        >
                          <span className="tienda-modal__cc-flag">{c.flag}</span>
                          <span className="tienda-modal__cc-optname">
                            {c.country}
                            {c.iso ? ` (${c.iso})` : ""}
                          </span>
                          <span className="tienda-modal__cc-dial">{c.value}</span>
                          {selected && (
                            <span
                              className="tienda-modal__cc-check"
                              aria-hidden="true"
                            >
                              ✓
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="tienda-modal__field">
              <label className="tienda-modal__label">Teléfono</label>
              <input
                name="telefono"
                value={form.telefono}
                onChange={updateField}
                disabled={saving}
                inputMode="tel"
                maxLength={phoneMax}
                placeholder={phoneMax ? `Hasta ${phoneMax} dígitos` : ""}
                className="tienda-modal__input"
              />
              {selectedCountry && (phoneMin || phoneMax) && (
                <span className="tienda-modal__help">
                  {selectedCountry.flag} {selectedCountry.country}:{" "}
                  {phoneMin ? `min ${phoneMin}` : ""}
                  {phoneMin && phoneMax ? " / " : ""}
                  {phoneMax ? `max ${phoneMax}` : ""} dígitos
                </span>
              )}
            </div>

          <div className="tienda-modal__field tienda-modal__field--full">
            <label className="tienda-modal__label">Correo</label>
            <input
              type="email"
              name="correo"
              value={form.correo}
              onChange={updateField}
              disabled={saving}
              className="tienda-modal__input"
            />
          </div>
          </div>

          <div className="tienda-modal__footer">
            <button
              type="button"
              onClick={onClose}
              className="tienda-modal__btn tienda-modal__btn--secondary"
              disabled={saving}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="tienda-modal__btn tienda-modal__btn--primary"
              disabled={saving}
            >
              {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CustomerFormModal;
